import type {
  ChipOrigin,
  ChipSurface,
  MessageEntryMethod,
} from '@heidi/evidence/contracts';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

// ============================================================================
// Chat Message Queue
// ============================================================================
// Cursor-style message queue that stores messages sent while streaming.
// Users can "Send now" (interrupt current and send), or let messages auto-send.

/**
 * File metadata for pre-uploaded attachments.
 */
export interface QueuedFileMetadata {
  name: string;
  type: string;
  url?: string;
  attachment_id?: string;
}

/**
 * A queued message waiting to be sent.
 */
export interface QueuedMessage {
  /** Unique identifier for the queued message */
  id: string;
  /** The message content */
  content: string;
  /**
   * @deprecated Use `attachmentIds` and `fileMetadata` instead.
   * Raw file attachments were used before the pre-upload flow was implemented.
   * Files are now uploaded immediately via useChatFileUpload, and only the
   * resulting attachment IDs are passed to the chat API.
   *
   * Migration: Remove this property once all callers use the pre-upload flow.
   */
  files?: File[];
  /** Pre-uploaded attachment IDs from /chat/attachments endpoint */
  attachmentIds?: string[];
  /** File metadata for displaying attachment previews in queued messages */
  fileMetadata?: QueuedFileMetadata[];
  /** How the message was initiated (for analytics) */
  entryMethod?: MessageEntryMethod;
  /** Chip display label for analytics (shortcut/suggestion only) */
  chipLabel?: string;
  /** Chip category for analytics (shortcut/suggestion only) */
  chipCategory?: string;
  /** Shortcut origin bucket (only meaningful for shortcut_chip entryMethod). */
  chipOrigin?: ChipOrigin;
  /** UI surface the chip was triggered from. */
  chipSurface?: ChipSurface;
  /** Timestamp when queued */
  queuedAt: number;
}

// Queue items always originate from an auto-send/sendNow dispatch, so
// `queued` is always `true` when a queued message is eventually sent. The
// dispatch decision is made in `useChatQueue` (queueOrSend / auto-send /
// sendNow) rather than stored on the item itself.

/**
 * Queue state for a specific chat.
 */
export interface ChatQueueState {
  /** Messages waiting to be sent (FIFO order) */
  messages: QueuedMessage[];
  /** Whether auto-send is enabled (send next when streaming completes) */
  autoSendEnabled: boolean;
}

const DEFAULT_QUEUE_STATE: ChatQueueState = {
  messages: [],
  autoSendEnabled: true,
};

/**
 * Atom family for per-chat queue state.
 * Key is the chat stateKey (e.g., "chat-123" or "sessionless").
 */
export const chatQueueAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_stateKey: string) => atom<ChatQueueState>(DEFAULT_QUEUE_STATE)
);

/**
 * Generate a unique ID for queued messages.
 */
export const generateQueueId = () =>
  `queue-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ============================================================================
// Derived Atoms
// ============================================================================

/**
 * Get queue for a specific chat.
 */
export const getChatQueueAtom = (stateKey: string) => chatQueueAtomFamily(stateKey);

/**
 * Write-only atom to add a message to the queue.
 */
export const addToQueueAtom = atom(
  null,
  (
    get,
    set,
    params: {
      stateKey: string;
      content: string;
      files?: File[];
      attachmentIds?: string[];
      fileMetadata?: QueuedFileMetadata[];
      entryMethod?: MessageEntryMethod;
      chipLabel?: string;
      chipCategory?: string;
      chipOrigin?: ChipOrigin;
      chipSurface?: ChipSurface;
    }
  ) => {
    const {
      stateKey,
      content,
      files,
      attachmentIds,
      fileMetadata,
      entryMethod,
      chipLabel,
      chipCategory,
      chipOrigin,
      chipSurface,
    } = params;
    const queueAtom = chatQueueAtomFamily(stateKey);
    const current = get(queueAtom);

    const newMessage: QueuedMessage = {
      id: generateQueueId(),
      content,
      files,
      attachmentIds,
      fileMetadata,
      entryMethod,
      chipLabel,
      chipCategory,
      chipOrigin,
      chipSurface,
      queuedAt: Date.now(),
    };

    set(queueAtom, {
      ...current,
      messages: [...current.messages, newMessage],
    });

    return newMessage.id;
  }
);

/**
 * Write-only atom to remove a message from the queue.
 */
export const removeFromQueueAtom = atom(
  null,
  (get, set, params: { stateKey: string; messageId: string }) => {
    const { stateKey, messageId } = params;
    const queueAtom = chatQueueAtomFamily(stateKey);
    const current = get(queueAtom);

    set(queueAtom, {
      ...current,
      messages: current.messages.filter((m) => m.id !== messageId),
    });
  }
);

/**
 * Write-only atom to pop the first message from the queue.
 * Returns the message or null if queue is empty.
 */
export const popFromQueueAtom = atom(null, (get, set, stateKey: string) => {
  const queueAtom = chatQueueAtomFamily(stateKey);
  const current = get(queueAtom);

  if (current.messages.length === 0) {
    return null;
  }

  const [first, ...rest] = current.messages;

  set(queueAtom, {
    ...current,
    messages: rest,
  });

  return first;
});

/**
 * Write-only atom to toggle auto-send for a chat queue.
 */
export const toggleAutoSendAtom = atom(
  null,
  (get, set, params: { stateKey: string; enabled?: boolean }) => {
    const { stateKey, enabled } = params;
    const queueAtom = chatQueueAtomFamily(stateKey);
    const current = get(queueAtom);

    set(queueAtom, {
      ...current,
      autoSendEnabled: enabled ?? !current.autoSendEnabled,
    });
  }
);

/**
 * Write-only atom to clear the entire queue for a chat.
 */
export const clearQueueAtom = atom(null, (get, set, stateKey: string) => {
  const queueAtom = chatQueueAtomFamily(stateKey);
  set(queueAtom, DEFAULT_QUEUE_STATE);
});

/**
 * Write-only atom to update a queued message's content.
 */
export const updateQueuedMessageAtom = atom(
  null,
  (get, set, params: { stateKey: string; messageId: string; content: string }) => {
    const { stateKey, messageId, content } = params;
    const queueAtom = chatQueueAtomFamily(stateKey);
    const current = get(queueAtom);

    set(queueAtom, {
      ...current,
      messages: current.messages.map((m) => (m.id === messageId ? { ...m, content } : m)),
    });
  }
);

/**
 * Write-only atom to move a queued message to the front (for "send now").
 */
export const promoteQueuedMessageAtom = atom(
  null,
  (get, set, params: { stateKey: string; messageId: string }) => {
    const { stateKey, messageId } = params;
    const queueAtom = chatQueueAtomFamily(stateKey);
    const current = get(queueAtom);

    const messageIndex = current.messages.findIndex((m) => m.id === messageId);
    if (messageIndex <= 0) return; // Already first or not found

    const message = current.messages[messageIndex];
    if (!message) return; // Guard against undefined

    const otherMessages = current.messages.filter((m) => m.id !== messageId);

    set(queueAtom, {
      ...current,
      messages: [message, ...otherMessages],
    });
  }
);
